import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import Analysis, { AnalysisCreate, AnalysisUpdate } from '../../interfaces/analysis.interface';
import { backend, CustomError, validation } from '../../utils';
import { authState$ } from '../../utils/backend/backend.util';

/**
 * The data$ subject contains the user's analyses after the user has been logged
 * in and the analyses have been retrieved from the backend. This field is
 * generally not intended to be used directly.
 */
const data$ = new BehaviorSubject<Analysis[]>([]);

/**
 * Get all analyses.
 *
 * @example
 *  analyses$.subscribe(...);
 *  const { value: analyses, error, isLoading } = useObservable(analyses$, []);
 */
export const analyses$: Observable<Analysis[]> = data$.asObservable();

/**
 * Get the analysis by the given id.
 *
 * @example
 *  read$(id).subscribe(...);
 *  const { value: analysis, error, isLoading } = useObservable(read$(id));
 */
export const read$ = (id: Analysis['id']): Observable<Analysis | undefined> =>
  data$.pipe(
    map((analyses: Analysis[]) => {
      const result = analyses.find((analysis) => analysis.id === id);

      if (!result) {
        throw new CustomError({ ...CustomError.errors.backend.DataNotFound });
      }

      return result;
    })
  );

/**
 * Save (create/update) a given analysis and return the document's id
 *
 * @example
 *  await saveAnalysis$(
 *    {
 *      id: 'gEJxNF2l0HaO4dAf1J3j',
 *      name: 'Test Analysis' ,
 *      owner: 'R1rXq1PCOQYP9R00u4MHQ1o2eg31',
 *      status: 'analyzing',
 *      tags: ['test']
 *    },
 *    false
 *  ) => updates the analysis
 *
 *  await saveAnalysis$(
 *    {
 *      name: 'Test Analysis' ,
 *      owner: 'R1rXq1PCOQYP9R00u4MHQ1o2eg31',
 *      tags: ['test']
 *    },
 *    true
 *  ) => creates a new analysis
 */
export const save$ = async (
  analysis: AnalysisCreate | AnalysisUpdate,
  create = false
): Promise<string> => {
  await backend.isInitialized$;

  try {
    create
      ? validation.analysisCreate.validateSync(analysis)
      : validation.analysisUpdate.validateSync(analysis);
  } catch (error) {
    throw new CustomError({
      ...CustomError.errors.user.DataInvalidAnalysisData,
      originalError: error,
    });
  }

  // Optional values on update
  const { pid, status } = analysis;

  return backend.saveDocument$<AnalysisCreate | Omit<AnalysisUpdate, 'id'>>({
    collection: 'analyses',
    ...(create ? {} : { doc: (analysis as AnalysisUpdate).id }),
    data: {
      name: analysis.name,
      tags: analysis.tags,
      owner: analysis.owner,
      ...(create
        ? {
            pid: '',
            image: '',
            model: '',
            status: 'created',
            capability: 0,
            /** @todo consider using `results: []` (vs orientability, ...)  */
            separability: {
              confidence: 0,
              dataset: [],
              value: 0,
              models: {
                easy: '',
                medium: '',
                hard: '',
              },
            },
            orientabilityL1: {
              confidence: 0,
              dataset: [],
              value: 0,
              models: {
                easy: '',
                medium: '',
                hard: '',
              },
            },
            orientabilityL2: {
              confidence: 0,
              dataset: [],
              value: 0,
              models: {
                easy: '',
                medium: '',
                hard: '',
              },
            },
            orientabilityL3: {
              confidence: 0,
              dataset: [],
              value: 0,
              models: {
                easy: '',
                medium: '',
                hard: '',
              },
            },
            irNecessity: {
              confidence: 0,
              dataset: [],
              value: 0,
              models: {
                easy: '',
                medium: '',
                hard: '',
              },
            },
            tangibility: {
              confidence: 0,
              dataset: [],
              value: 0,
              models: {
                easy: '',
                medium: '',
                hard: '',
              },
            },
          }
            
        : {
            ...(pid ? { pid } : {}),
            ...(status ? { status } : {}),
          }),
    },
    ...(create
      ? {}
      : {
          options: {
            mergeFields: ['name', 'tags', 'updatedAt', status && 'status', pid && 'pid'].filter(
              Boolean
            ) as string[],
          },
        }),
  });
};

/**
 * Initialize the service (this is done automatically) by listening to authState
 * changes (login/logout)
 */
const init = (): Subscription =>
  authState$
    .pipe(
      switchMap((user) => {
        if (!user) {
          return of([]);
        }

        return backend.queryCollection$<Analysis>({
          collection: 'analyses',
          where: [['owner', '==', user.uid]],
          order: [['createdAt', 'desc']],
        });
      })
    )
    .subscribe(data$);

// Auto init
init();

/** Some useful exports for testing purposes only */
export const testing = {
  data$,
  init,
};
